// Board-image coordinates (2000 x 1412, see ../boardGeometry.ts) mapped onto the 3D world:
// x runs bow (−) → stern (+), z runs port / "upper" side (−) → starboard / "lower" side (+).
import * as THREE from "three";

import type { SpaceId } from "../../../../../common/unfathomable";
import { kSpaceGeom, polyCentroid } from "../boardGeometry";

export const kScale = 80;
const kCx = 1000;
const kCz = 917;

export const kDeckY = 0.55;

export function toWorld(bx: number, by: number, y = 0): THREE.Vector3 {
  return new THREE.Vector3((bx - kCx) / kScale, y, (by - kCz) / kScale);
}

export function toXZ(bx: number, by: number): [number, number] {
  return [(bx - kCx) / kScale, (by - kCz) / kScale];
}

/**
 * A THREE.Shape for points in the XZ plane. Shapes use (x, −z) so that a mesh rotated by
 * −π/2 about X lies flat with its face (and any extrusion) pointing up.
 */
export function shapeFromXZ(pts: [number, number][]): THREE.Shape {
  return new THREE.Shape(pts.map(([x, z]) => new THREE.Vector2(x, -z)));
}

export function spacePolyXZ(sp: SpaceId): [number, number][] {
  return kSpaceGeom[sp].poly.map(([x, y]) => toXZ(x, y));
}

export function spaceShape(sp: SpaceId): THREE.Shape {
  return shapeFromXZ(spacePolyXZ(sp));
}

export function spaceCentre(sp: SpaceId): [number, number] {
  return toXZ(...polyCentroid(kSpaceGeom[sp].poly));
}

export function peopleAnchor(sp: SpaceId): [number, number] {
  return toXZ(...kSpaceGeom[sp].people);
}

export function monsterAnchor(sp: SpaceId): [number, number] {
  return toXZ(...kSpaceGeom[sp].monsters);
}

/** The Deep: a whirlpool out on the open sea beyond the port side, well clear of the hull and water spaces. */
export const kDeepXZ: [number, number] = [-2, -11];

/** The ship's outer outline (deck edge), bow at −x. */
export const kHullOutline: [number, number][] = ([
  [30, 915], [300, 760], [580, 645], [1040, 640], [1490, 640], [1700, 660], [1900, 760], [1950, 915],
  [1900, 1070], [1700, 1170], [1490, 1190], [1040, 1190], [580, 1190], [300, 1070],
] as [number, number][]).map(([x, y]) => toXZ(x, y));

export function isWater(sp: string): boolean {
  return sp.startsWith("W");
}
